import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RichTextDisplay } from '../components/ui/rich-text-display';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const defaultContent = `<h2>Condizioni Generali di Vendita</h2>
<p>Le presenti Condizioni Generali di Vendita disciplinano la vendita dei prodotti e servizi offerti da Gmed Srl.</p>

<h3>1. Oggetto</h3>
<p>Le presenti condizioni regolano la vendita di prodotti e la fornitura di servizi IT da parte di Gmed Srl ai propri clienti.</p>

<h3>2. Ordini</h3>
<p>Gli ordini si intendono accettati solo dopo conferma scritta da parte di Gmed Srl. L'azienda si riserva il diritto di rifiutare ordini senza obbligo di motivazione.</p>

<h3>3. Prezzi e Pagamenti</h3>
<p>I prezzi sono quelli indicati nel preventivo o nel listino vigente al momento dell'ordine. Salvo diverso accordo, il pagamento deve avvenire entro 30 giorni dalla data della fattura.</p>

<h3>4. Consegna</h3>
<p>I termini di consegna indicati sono puramente indicativi. Eventuali ritardi non danno diritto a risarcimenti o penali, salvo diverso accordo scritto.</p>

<h3>5. Garanzia</h3>
<p>I prodotti sono coperti dalla garanzia legale di conformità. Per i servizi, si applicano le garanzie specificate nel contratto di fornitura.</p>

<h3>6. Limitazione di Responsabilità</h3>
<p>Gmed Srl non sarà responsabile per danni indiretti, perdite di dati o mancati guadagni derivanti dall'uso dei prodotti o servizi forniti.</p>

<h3>7. Proprietà Intellettuale</h3>
<p>Tutti i diritti di proprietà intellettuale sui software e materiali forniti rimangono di proprietà di Gmed Srl o dei rispettivi licenzianti.</p>

<h3>8. Privacy</h3>
<p>I dati personali saranno trattati in conformità con la nostra Informativa Privacy e il GDPR.</p>

<h3>9. Foro Competente</h3>
<p>Per qualsiasi controversia sarà competente in via esclusiva il Foro di Siena.</p>

<h3>10. Contatti</h3>
<p><strong>Gmed Srl</strong><br/>
Strada di Selvamaggio, 19<br/>
53034 - Colle di Val d'Elsa (SI)<br/>
Email: comunicazioni@gmed.it</p>`;

export const CondizioniVendita = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/pages/condizioni-vendita`);
      setPageData(response.data);
    } catch (error) {
      setPageData(null);
    } finally {
      setLoading(false);
    }
  };

  const title = pageData?.title || 'Condizioni di Vendita';
  const content = pageData?.content || defaultContent;
  const pdfUrl = pageData?.pdf_url;

  return (
    <div className="min-h-screen pt-20" data-testid="condizioni-vendita-page">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-red-600 flex items-center justify-center mx-auto mb-6 shadow-lg`}>
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display font-bold text-4xl lg:text-5xl mb-4 text-white">
              {title}
            </h1>
            <p className="text-lg text-gray-300">
              Termini e condizioni per l'acquisto di prodotti e servizi
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="glass-card">
                <CardContent className="p-8 md:p-12">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <>
                      {pdfUrl && (
                        <div className="mb-8 p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-6 h-6 text-primary" />
                            <div>
                              <p className="font-medium">Documento PDF disponibile</p>
                              <p className="text-sm text-muted-foreground">Scarica la versione completa in PDF</p>
                            </div>
                          </div>
                          <Button asChild>
                            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                              <Download className="w-4 h-4 mr-2" />
                              Scarica PDF
                            </a>
                          </Button>
                        </div>
                      )}
                      
                      <div className="prose prose-invert max-w-none">
                        <RichTextDisplay content={content} />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CondizioniVendita;
